import * as Yup from 'yup';

const objectIdRegex = /^[a-f\d]{24}$/i;

const defaultFacilityInventoryItem = {
    property_id: '',
    building_name: '',
    items: [
        {
            name: '',
            make: '',
            modelNo: '',
            requestsSupported: [],
            quantity: '',
            costPerUnit: '',
            warrantyDetails: [
                {
                    boughtOn: undefined,
                    boughtFrom: undefined,
                    warrantyExpirationDate: '',
                },
            ],
            locationDetails: {
                room: '',
                notes: '',
            },
            images: [
                {
                    url: '',
                    uploadedBy: {
                        id: '',
                        role: '',
                    },
                },
            ],
            invoices: [
                {
                    url: '',
                    uploadedBy: {
                        id: '',
                        role: '',
                    },
                },
            ],
            status: '',
        },
    ],
};

export const facilityInventoryItemSchema = Yup.object().shape({
    property_id: Yup.string()
        .test('is-object-id', 'Invalid Property ID', value => !value || objectIdRegex.test(value))
        .required('Property ID is required'),
    building_name: Yup.string().required('Building name is required'),
    items: Yup.array().of(
        Yup.object().shape({
            name: Yup.string().required('Item name is required'),
            make: Yup.string().optional(),
            modelNo: Yup.string().optional(),
            requestsSupported: Yup.array()
                .of(
                    Yup.string()
                        .test('is-object-id', 'Invalid Request Type ID', value => !value || objectIdRegex.test(value))
                        .required('Request Type ID is required')
                )
                .min(1, 'At least one request type is required')
                .required('Request Types are required'),
            quantity: Yup.number()
                .required('Quantity is required')
                .min(0, 'Quantity cannot be negative'),
            costPerUnit: Yup.number()
                .required('Cost per unit is required')
                .min(0, 'Cost cannot be negative'),
            warrantyDetails: Yup.array()
                .of(
                    Yup.object().shape({
                        boughtOn: Yup.date().nullable().optional(),
                        boughtFrom: Yup.string().optional(),
                        warrantyExpirationDate: Yup.date().nullable().optional(),
                    })
                )
                .optional(),
            locationDetails: Yup.object().shape({
                room: Yup.string().optional(),
                notes: Yup.string().optional(),
            }).optional(),
            images: Yup.array().of(
                Yup.object().shape({
                    url: Yup.string().url('Invalid image URL').required('Image URL is required'),
                    uploadedBy: Yup.object().shape({
                        id: Yup.string()
                            .test('is-object-id', 'Invalid ID', value => !value || objectIdRegex.test(value))
                            .optional(),
                        role: Yup.string().optional(),
                    }),
                })
            ),
            invoices: Yup.array().of(
                Yup.object().shape({
                    url: Yup.string().url('Invalid invoice URL').required('Invoice URL is required'),
                    uploadedBy: Yup.object().shape({
                        id: Yup.string()
                            .test('is-object-id', 'Invalid ID', value => !value || objectIdRegex.test(value))
                            .optional(),
                        role: Yup.string().optional(),
                    }),
                })
            ),
            status: Yup.string().oneOf(['active', 'inactive', 'under_maintenance', 'retired'], 'Invalid status').required('Status is required'),
        })
    ).min(1, 'At least one item is required'),
});

export const updateFacilityInventoryItemSchema = Yup.object().shape({
    item_id: Yup.string()
        .test('is-object-id', 'Invalid item ID', value => !value || objectIdRegex.test(value))
        .required('Item ID is required'),
    item: Yup.object().shape({
        name: Yup.string().optional(),
        make: Yup.string().optional(),
        modelNo: Yup.string().optional(),
        requestsSupported: Yup.array()
            .of(
                Yup.string()
                    .test('is-object-id', 'Invalid Request Type ID', value => !value || objectIdRegex.test(value))
            )
            .optional(),
        quantity: Yup.number()
            .optional()
            .min(0, 'Quantity cannot be negative'),
        costPerUnit: Yup.number()
            .optional()
            .min(0, 'Cost cannot be negative'),
        warrantyDetails: Yup.array().of(
            Yup.object().shape({
                boughtOn: Yup.date().nullable().optional(),
                boughtFrom: Yup.string().optional(),
                warrantyExpirationDate: Yup.date().nullable().optional(),
            })
        ).optional(),
        locationDetails: Yup.object().shape({
            room: Yup.string().optional(),
            notes: Yup.string().optional(),
        }).optional(),
        images: Yup.array().of(
            Yup.object().shape({
                url: Yup.string().url('Invalid image URL').optional(),
                uploadedBy: Yup.object().shape({
                    id: Yup.string()
                        .test('is-object-id', 'Invalid ID', value => !value || objectIdRegex.test(value))
                        .optional(),
                    role: Yup.string().optional(),
                }).optional(),
            })
        ).optional(),
        invoices: Yup.array().of(
            Yup.object().shape({
                url: Yup.string().url('Invalid invoice URL').optional(),
                uploadedBy: Yup.object().shape({
                    id: Yup.string()
                        .test('is-object-id', 'Invalid ID', value => !value || objectIdRegex.test(value))
                        .optional(),
                    role: Yup.string().optional(),
                }).optional(),
            })
        ).optional(),
        status: Yup.string().oneOf(['active', 'inactive', 'under_maintenance', 'retired'], 'Invalid status').optional(),
    }),
    property_id: Yup.string()
        .test('is-object-id', 'Invalid Property ID', value => !value || objectIdRegex.test(value))
        .optional(),
    building_name: Yup.string().optional(),
});

export const getValidationSchema = (modal) => {
    if (modal === 'add') {
        return facilityInventoryItemSchema;
    } else if (modal === 'edit') {
        return updateFacilityInventoryItemSchema;
    }
    return facilityInventoryItemSchema;
};

export const getInitialValues = (modal, selectedItem, preservedValues = {}) => {
    if (modal === 'add') {
        return {
            ...defaultFacilityInventoryItem,
            ...preservedValues,
        };
    } else if (modal === 'edit' && selectedItem) {
        return {
            item_id: selectedItem._id || '',
            item: {
                name: selectedItem.name || '',
                make: selectedItem.make || '',
                modelNo: selectedItem.modelNo || '',
                requestsSupported: selectedItem.requestsSupported
                    ? selectedItem.requestsSupported.map(type => type._id).filter(Boolean)
                    : [],
                quantity: selectedItem.quantity || 0,
                costPerUnit: selectedItem.costPerUnit || 0,
                warrantyDetails: selectedItem.warrantyDetails || [
                    {
                        boughtOn: undefined,
                        boughtFrom: undefined,
                        warrantyExpirationDate: '',
                    },
                ],
                locationDetails: {
                    room: selectedItem.locationDetails?.room || '',
                    notes: selectedItem.locationDetails?.notes || '',
                },
                images: selectedItem.images || [],
                invoices: selectedItem.invoices || [],
                status: selectedItem.status || 'active',
            },
            property_id: preservedValues.property_id || selectedItem.property_id || '',
            building_name: preservedValues.building_name || selectedItem.building_name || '',
        };
    }

    return {
        ...defaultFacilityInventoryItem,
        ...preservedValues,
    };
};